<template>
    <div>
        <div class="dashboard">
            <h1>Patient Dashboard</h1>
            <b-card footer-class="p-0">
                <b-card-group deck>
                    <b-card no-body class="border-0">
                        <distribution-by-species />
                    </b-card>
                    <b-card no-body class="border-0">
                        <new-patients />
                    </b-card>
                    <b-card no-body class="border-0">
                        <total-active-patients />
                    </b-card>
                </b-card-group>

                <template v-slot:footer>
                    <b-tabs
                        card
                        class="list"
                        content-class="dashboard-list"
                        active
                    >
                        <!-------- Patient List -------->
                        <b-tab
                            title-link-class="p-0"
                            lazy
                            :key="1 + keyComponent"
                            :active="selectedTab === TAB.PATIENTS"
                        >
                            <template v-slot:title>
                                <b-nav-item-dropdown>
                                    <template slot="button-content">
                                        <i class="cus-page-white-tux"></i>
                                        Patient List
                                    </template>
                                    <b-dropdown-item
                                        @click="
                                            changePatientListView(constant.ALL)
                                        "
                                    >
                                        <i class="cus-tux"></i>
                                        View All Patients ({{
                                            patientListCounts.ALL
                                        }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changePatientListView(
                                                constant.ACTIVE
                                            )
                                        "
                                    >
                                        <i class="cus-accept"></i>
                                        View Active Patients ({{
                                            patientListCounts.ACTIVE
                                        }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changePatientListView(
                                                constant.DECEASED
                                            )
                                        "
                                    >
                                        <i class="cus-heart"></i>
                                        View Deceased Patients ({{
                                            patientListCounts.DECEASED
                                        }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changePatientListView(
                                                constant.INACTIVE
                                            )
                                        "
                                    >
                                        <i class="cus-cross"></i>
                                        View Inactive Patients ({{
                                            patientListCounts.INACTIVE
                                        }})
                                    </b-dropdown-item>
                                </b-nav-item-dropdown>
                            </template>
                            <b-card-text>
                                <patient-list
                                    :filter="tabPatientListStatus"
                                    :isUsingViewProfile="USE_PATIENT_MODULE_VUE"
                                />
                            </b-card-text>
                        </b-tab>

                        <!-------- Unassigned Labs -------->
                        <b-tab
                            v-if="isShowUnassignLabs()"
                            lazy
                            @click="labClick"
                        >
                            <template v-slot:title>
                                <i class="cus-book-error"></i>Labs
                                <span><i class="cus-bullet-go"></i></span>
                            </template>
                        </b-tab>

                        <!-------- Unassigned Images -------->
                        <b-tab
                            v-if="isShowUnassignImages()"
                            :active="selectedTab === TAB.UNASSIGNED_IMAGES"
                            :key="3 + keyComponent"
                            lazy
                        >
                            <template v-slot:title>
                                <i class="cus-picture-error"></i>Unassigned
                                Images
                            </template>
                            <b-card-text>
                                <unassigned-images
                                    :businessId="settings.business_id || ''"
                                    :isUsingViewProfile="USE_PATIENT_MODULE_VUE"
                                />
                            </b-card-text>
                        </b-tab>

                        <!-------- Custom Diagnoses -------->
                        <b-tab
                            title-link-class="p-0"
                            :active="selectedTab === TAB.CUSTOM_DIAGNOSES"
                            :key="4 + keyComponent"
                            lazy
                        >
                            <template v-slot:title>
                                <b-nav-item-dropdown>
                                    <template slot="button-content">
                                        <i class="cus-stethoscope"></i>
                                        Custom Diagnoses
                                    </template>
                                    <b-dropdown-item
                                        @click="
                                            changeCustomDiagnosesView(
                                                constant.ACTIVE
                                            )
                                        "
                                    >
                                        <i class="cus-stethoscope"></i>View
                                        Active Diagnoses
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changeCustomDiagnosesView(
                                                constant.SUSPENDED
                                            )
                                        "
                                    >
                                        <i class="cus-stethoscope"></i>View
                                        Suspended Diagnoses
                                    </b-dropdown-item>
                                    <div class="dropdown-divider"></div>
                                    <b-dropdown-item
                                        href="#"
                                        @click.prevent="openNewDiagnosisDialog"
                                    >
                                        <i class="cus-add"></i>
                                        New Diagnosis
                                    </b-dropdown-item>
                                </b-nav-item-dropdown>
                            </template>
                            <b-card-text>
                                <custom-diagnoses
                                    ref="customDiagnoses"
                                    :filter="tabCustomDiagnoseStatus"
                                    @editDiagnosis="editDiagnosis"
                                />
                            </b-card-text>
                        </b-tab>

                        <!-- Rx Request -->
                        <b-tab
                            v-if="petCareEnabled || onlinePharmacyEnabled"
                            title-link-class="p-0"
                            :active="selectedTab === TAB.RX_REQUEST"
                            lazy
                        >
                            <template v-slot:title>
                                <b-nav-item-dropdown>
                                    <template slot="button-content">
                                        <span name="tab-refill">
                                            <i
                                                class="cus-basket-put"
                                                :class="{
                                                    'nested-blink-indicator': hasPendingRequest
                                                }"
                                            ></i>
                                            Rx Request
                                        </span>
                                    </template>
                                    <b-dropdown-item
                                        @click="changeRxRequestCategory()"
                                    >
                                        <i class="cus-basket-put"></i>View All
                                        Request ({{
                                            rxRequestCount.pending +
                                            rxRequestCount.approved +
                                            rxRequestCount.rejected +
                                            rxRequestCount.completed
                                        }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changeRxRequestCategory(
                                                rxConstant.PENDING_REQUEST
                                            )
                                        "
                                    >
                                        <i class="cus-error"></i>View Pending
                                        Request ({{ rxRequestCount.pending }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changeRxRequestCategory(
                                                rxConstant.APPROVED_REQUEST
                                            )
                                        "
                                    >
                                        <i class="cus-accept"></i>View Approved
                                        Request ({{ rxRequestCount.approved }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changeRxRequestCategory(
                                                rxConstant.REJECTED_REQUEST
                                            )
                                        "
                                    >
                                        <i class="cus-cancel"></i>View Denied
                                        Request ({{ rxRequestCount.rejected }})
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="
                                            changeRxRequestCategory(
                                                rxConstant.COMPLETED_REQUEST
                                            )
                                        "
                                    >
                                        <i class="cus-tick"></i>View Completed
                                        Request ({{ rxRequestCount.completed }})
                                    </b-dropdown-item>
                                </b-nav-item-dropdown>
                            </template>
                            <b-card-text>
                                <rx-request-list
                                    :category="rxRequestCategory"
                                    :key="5 + keyComponent"
                                    @reload-tab-count="getRxRequestCount"
                                />
                            </b-card-text>
                        </b-tab>
                    </b-tabs>
                </template>
            </b-card>

            <modal-add-edit-diagnosis
                ref="modalDiagnosis"
                :reloadAfter="true"
                @reloadlist="reloadCustomDiagnosesList"
            />
        </div>
    </div>
</template>
<script>
import "@/jquery/jquery.flot";
import "@/jquery/jquery.flot.tooltip";
import "@/jquery/jquery.flot.pie";
import distributionBySpecies from "../report/module/patient/distribution_by_species";
import newPatients from "../report/module/patient/new_patients";
import totalActivePatients from "../report/module/patient/total_active_patients";
import patientList from "./dashboard/patient-list/list";
import customDiagnoses from "./dashboard/custom-diagnoses/list";
import modalAddEditDiagnosis from "./dashboard/custom-diagnoses/modal-new-edit";
import unassignedLabs from "./dashboard/unassigned-labs/list";
import unassignedImages from "./dashboard/unassigned-images/list";
import RxRequestList from "@/module/patient/dashboard/rx-request/rx-request-list";
import EXTENSIONS from "@/constants/extensions";
import Records from "@/services/records";
import REQUEST from "@/constants/refill-requests";

const ALL = "ALL";
const ACTIVE = "ACTIVE";
const DECEASED = "DECEASED";
const INACTIVE = "INACTIVE";
const SUSPENDED = "SUSPENDED";
const NEW = "NEW";
const EDIT = "EDIT";

// rx request status
const PENDING_REQUEST = 0;
const APPROVED_REQUEST = 1;
const REJECTED_REQUEST = 2;
const COMPLETED_REQUEST = 3;

const TAB = {
    PATIENTS: "PATIENTS",
    UNASSIGNED_LABS: "UNASSIGNEDLABS",
    UNASSIGNED_IMAGES: "UNASSIGNEDIMAGES",
    CUSTOM_DIAGNOSES: "CUSTOMDIAGNOSES",
    RX_REQUEST: "REFILLREQUEST"
};

export default {
    components: {
        unassignedImages,
        distributionBySpecies,
        newPatients,
        totalActivePatients,
        patientList,
        customDiagnoses,
        modalAddEditDiagnosis,
        RxRequestList
    },
    data() {
        return {
            constant: { ALL, ACTIVE, DECEASED, INACTIVE, SUSPENDED, NEW },
            rxConstant: {
                APPROVED_REQUEST,
                PENDING_REQUEST,
                REJECTED_REQUEST,
                COMPLETED_REQUEST
            },
            tabPatientListStatus: ALL,
            tabCustomDiagnoseStatus: ACTIVE,
            resources: {},
            count: {},
            patientListCounts: {
                ALL: 0,
                ACTIVE: 0,
                DECEASED: 0,
                INACTIVE: 0
            },
            modalDiagnosisMode: NEW,
            modalDiagnosisRecord: null,
            settings: {},
            activeTabIndex: 0,
            isRendered: false,
            keyComponent: 0,
            selectedTab: null,
            rxRequestCategory: -1,
            TAB,
            hasPendingRequest: false,
            rxRequestCountLoaded: false,
            rxRequestCount: {
                pending: 0,
                approved: 0,
                rejected: 0,
                completed: 0
            },
            REQUEST
        };
    },
    computed: {
        onlinePharmacyEnabled() {
            return (
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_VETSOURCE) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_VETCOVE_ONLINE)
            );
        },
        petCareEnabled() {
            return this._isExtensionEnabled(
                EXTENSIONS.CODE.CODE_CLIENT_MOBILE_APP
            );
        }
    },
    watch: {
        onlinePharmacyEnabled: {
            handler(value) {
                if (value && !this.rxRequestCountLoaded) {
                    this.getRxRequestCount();
                }
            },
            immediate: true
        },
        petCareEnabled: {
            handler(value) {
                if (value && !this.rxRequestCountLoaded) {
                    this.getRxRequestCount();
                }
            },
            immediate: true
        }
    },
    created() {},
    mounted() {
        let self = this;

        this.applyPatientCounts();
        this.getSetting().then(() => {
            this.$nextTick(() => {
                setTimeout(function () {
                    self.checkLoadTab();
                }, 300);
            });
        });
    },
    methods: {
        checkLoadTab() {
            let routeQuery = this.$route.query;

            if (routeQuery && routeQuery.tab) {
                this.selectedTab = routeQuery.tab.toUpperCase();
                this.keyComponent++;
            }
        },
        async getSetting() {
            this.settings = await this._getSetting(); // origin: mixin
        },
        async applyPatientCounts() {
            try {
                const res = await this.$http.get(
                    process.env.BARRAMUNDI + "patient/patient-count"
                );

                if (res.data.response.message.code === 200) {
                    const counts = res.data.response.resources;

                    this.patientListCounts = {
                        ALL: counts.total_patients,
                        ACTIVE: counts.active_patient_count,
                        INACTIVE: counts.inactive_patient_count,
                        DECEASED: counts.deceased_patient_count
                    };
                } else {
                    throw new Error("Error: Unable to get patient counts");
                }
            } catch (e) {
                // handle error
            }
        },
        changePatientListView(type) {
            this.tabPatientListStatus = type;
        },
        changeCustomDiagnosesView(type) {
            this.tabCustomDiagnoseStatus = type;
        },
        openNewDiagnosisDialog() {
            this.$refs.modalDiagnosis.open({
                mode: NEW
            });
        },
        closeModalDiagnosis() {
            this.$refs.modalDiagnosis.close();
        },
        editDiagnosis(record) {
            /**
             * why edit diagnosis function is here, instead of in the list?
             * because: edit diagnosis use the same dialog as adding new diagnosis.
             * since adding a new diagnosis could be done outside the diagnoses list/view
             * thus the modal has to be placed inside parent component (this file)
             */
            this.$refs.modalDiagnosis.open({
                mode: EDIT,
                record: record
            });
        },
        reloadCustomDiagnosesList() {
            this.tabCustomDiagnoseStatus = this.constant.ACTIVE;
            this.$refs.customDiagnoses.fetchList(this.tabCustomDiagnoseStatus);
        },
        isShowUnassignLabs() {
            let code = EXTENSIONS.CODE;

            if (
                this._isExtensionEnabled(code.CODE_ONPREM) ||
                this._isExtensionEnabled(code.CODE_ABAXIS_API) ||
                this._isExtensionEnabled(code.CODE_ABAXIS_FUSE) ||
                this._isExtensionEnabled(code.CODE_ANTECH) ||
                this._isExtensionEnabled(
                    code.CODE_ANIMAL_REFERENCE_PATHOLOGY
                ) ||
                this._isExtensionEnabled(code.CODE_HESKA_CLOUD) ||
                this._isExtensionEnabled(code.CODE_IDEXX) ||
                this._isExtensionEnabled(code.MIDWEST) ||
                this._isExtensionEnabled(code.CODE_NBIOVET) ||
                this._isExtensionEnabled(code.CODE_ZNLABS) ||
                this._isExtensionEnabled(code.CODE_VETEK) ||
                this._isExtensionEnabled(code.CODE_MICROVET) ||
                this._isExtensionEnabled(code.CODE_BIONOTE)
            ) {
                return true;
            }

            return false;
        },
        isShowUnassignImages() {
            return (
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_ASTERIS) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_IDEXX_DR) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_VETOLOGY) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_SOUNDPACS)
            );
        },
        changeRxRequestCategory(val) {
            this.rxRequestCategory = val;
            this.keyComponent++;
        },
        getRxRequestCount() {
            this.rxRequestCountLoaded = true;

            Records.getRxRequestsStatus()
                .then(response => {
                    response.resource.forEach(data => {
                        if (
                            data.label.toLowerCase() === REQUEST.LABEL.PENDING
                        ) {
                            this.rxRequestCount.pending = data.count;
                            if (this.rxRequestCount.pending > 0) {
                                this.hasPendingRequest = true;
                            } else {
                                this.hasPendingRequest = false;
                            }
                        }
                        if (
                            data.label.toLowerCase() === REQUEST.LABEL.REJECTED
                        ) {
                            this.rxRequestCount.rejected = data.count;
                        }
                        if (
                            data.label.toLowerCase() === REQUEST.LABEL.APPROVED
                        ) {
                            this.rxRequestCount.approved = data.count;
                        }
                        if (
                            data.label.toLowerCase() === REQUEST.LABEL.COMPLETED
                        ) {
                            this.rxRequestCount.completed = data.count;
                        }
                    });
                })
                .catch(error => {
                    console.error(error);
                });
        },
        labClick() {
            this.$router.push({ name: "lab_results" });
        }
    }
};
</script>
